import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  FiArrowLeft,
  FiCpu,
  FiZap,
  FiGlobe,
  FiCode,
  FiImage,
  FiMessageSquare,
  FiStar,
  FiCheck
} from 'react-icons/fi';

const AvailableModelsGuide = () => {
  const SHOW_MODEL_PRICING = false;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const aiModels = [
    {
      name: '302.AI',
      icon: '🤖',
      category: 'Multi-Model Platform',
      description: 'Advanced AI platform offering multiple models and capabilities in one place.',
      features: ['Multiple AI Models', 'Image Generation', 'Text Analysis', 'API Integration'],
      bestFor: 'Versatile AI tasks requiring multiple model types',
      pricing: 'Usage-based',
      popular: false
    },
    {
      name: 'DeepSeek',
      icon: '🔍',
      category: 'Code & Research',
      description: 'Specialized AI model focused on deep reasoning and code understanding.',
      features: ['Code Analysis', 'Deep Reasoning', 'Research Tasks', 'Problem Solving'],
      bestFor: 'Complex coding projects and research tasks',
      pricing: 'Free tier available',
      popular: true
    },
    {
      name: 'Ollama',
      icon: '🦙',
      category: 'Local Models',
      description: 'Run AI models locally on your machine for privacy and speed.',
      features: ['Local Processing', 'Privacy First', 'Offline Capable', 'Custom Models'],
      bestFor: 'Privacy-sensitive tasks and offline work',
      pricing: 'Free (local)',
      popular: false
    },
    {
      name: 'OpenAI',
      icon: '🧠',
      category: 'General Purpose',
      description: 'Industry-leading AI models including GPT-4 for general conversation and tasks.',
      features: ['GPT-4 & GPT-3.5', 'Natural Language', 'Code Generation', 'Creative Writing'],
      bestFor: 'General conversation, writing, and creative tasks',
      pricing: 'Pay-per-use',
      popular: true
    },
    {
      name: 'OpenRouter',
      icon: '🌐',
      category: 'Model Router',
      description: 'Access to multiple AI models through a unified API gateway.',
      features: ['Multiple Providers', 'Model Comparison', 'Cost Optimization', 'Unified API'],
      bestFor: 'Comparing different models and cost optimization',
      pricing: 'Variable by model',
      popular: false
    },
    {
      name: 'Gemini',
      icon: '💎',
      category: 'Google AI',
      description: 'Google\'s advanced AI model with strong multimodal capabilities.',
      features: ['Multimodal AI', 'Image Understanding', 'Long Context', 'Fast Processing'],
      bestFor: 'Multimodal tasks combining text and images',
      pricing: 'Freemium model',
      popular: true
    },
    {
      name: 'Claude',
      icon: '🎭',
      category: 'Anthropic AI',
      description: 'Constitutional AI focused on helpful, harmless, and honest responses.',
      features: ['Long Context', 'Document Analysis', 'Ethical AI', 'Reasoning'],
      bestFor: 'Document analysis and ethical AI interactions',
      pricing: 'Pay-per-use',
      popular: true
    },
    {
      name: 'Azure AI Foundry',
      icon: '☁️',
      category: 'Microsoft AI',
      description: 'Microsoft\'s enterprise AI platform with robust security and compliance.',
      features: ['Enterprise Security', 'Custom Models', 'Integration', 'Compliance'],
      bestFor: 'Enterprise applications and custom AI solutions',
      pricing: 'Enterprise pricing',
      popular: false
    }
  ];

  const modelCategories = [
    {
      name: 'General Purpose',
      description: 'Best for everyday conversations and general tasks',
      models: ['OpenAI', 'Claude', 'Gemini']
    },
    {
      name: 'Code & Development',
      description: 'Specialized for programming and technical tasks',
      models: ['DeepSeek', 'OpenAI', 'Claude']
    },
    {
      name: 'Privacy & Local',
      description: 'Run locally for maximum privacy',
      models: ['Ollama']
    },
    {
      name: 'Enterprise',
      description: 'Built for business and enterprise needs',
      models: ['Azure AI Foundry', '302.AI']
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-dark-900 via-dark-800 to-secondary-900 text-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Breadcrumb */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center text-gray-400 mb-8"
          >
            <Link to="/guide" className="hover:text-secondary-400 transition-colors duration-200">
              <FiArrowLeft className="w-4 h-4 mr-2 inline" />
              Guide
            </Link>
            <span className="mx-2">/</span>
            <span className="text-secondary-400">Available AI Models</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-secondary-500 to-accent-500 rounded-full mb-8"
            >
              <FiCpu className="w-10 h-10 text-white" />
            </motion.div>
            
            <h1 className="text-5xl md:text-6xl font-display font-bold mb-6 gradient-text">
              Available AI Models
            </h1>
            
            <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Explore the diverse range of AI models available in Bervin. Each model has unique 
              strengths and capabilities designed for different use cases and requirements.
            </p>

            <div className="flex items-center justify-center space-x-6 mt-8">
              <div className="flex items-center space-x-2 text-gray-400">
                <FiZap className="w-5 h-5" />
                <span>8 AI Models</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-400">
                <FiGlobe className="w-5 h-5" />
                <span>Multiple Providers</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Model Categories */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-4 gradient-text">
              Model Categories
            </h2>
            <p className="text-gray-300 text-lg">
              Choose the right AI model for your specific needs
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {modelCategories.map((category, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 * index }}
                className="bg-dark-800/50 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:border-secondary-500/50 transition-all duration-300"
              >
                <h3 className="text-xl font-semibold text-white mb-3">{category.name}</h3>
                <p className="text-gray-400 text-sm mb-4">{category.description}</p>
                <div className="space-y-2">
                  {category.models.map((model, modelIndex) => (
                    <div key={modelIndex} className="flex items-center text-sm text-gray-300">
                      <FiCheck className="w-3 h-3 text-secondary-400 mr-2" />
                      {model}
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Available Models */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-4 gradient-text">
              All Available Models
            </h2>
            <p className="text-gray-300 text-lg">
              Detailed overview of each AI model and its capabilities
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {aiModels.map((model, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 * index }}
                className="group relative"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-secondary-500/20 to-accent-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative bg-dark-800/50 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:border-secondary-500/50 transition-all duration-300">
                  <div className="flex items-start justify-between mb-6">
                    <div className="flex items-center">
                      <div className="text-3xl mr-4">{model.icon}</div>
                      <div>
                        <div className="flex items-center">
                          <h3 className="text-2xl font-bold text-white">{model.name}</h3>
                          {model.popular && (
                            <div className="ml-3 px-2 py-1 bg-gradient-to-r from-secondary-500 to-accent-500 rounded-full text-xs font-semibold text-white">
                              Popular
                            </div>
                          )}
                        </div>
                        <p className="text-secondary-400 font-medium">{model.category}</p>
                      </div>
                    </div>
                  </div>

                  <p className="text-gray-300 mb-6 leading-relaxed">{model.description}</p>

                  <div className="mb-6">
                    <h4 className="text-white font-semibold mb-3">Key Features:</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {model.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center text-sm text-gray-300">
                          <FiCheck className="w-3 h-3 text-secondary-400 mr-2 flex-shrink-0" />
                          {feature}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-6 border-t border-white/10">
                    <div>
                      <p className="text-xs text-gray-400 mb-1">Best for:</p>
                      <p className="text-sm text-gray-300">{model.bestFor}</p>
                    </div>
                    {SHOW_MODEL_PRICING && (
                      <div className="text-right">
                        <p className="text-xs text-gray-400 mb-1">Pricing:</p>
                        <p className="text-sm font-semibold text-secondary-400">{model.pricing}</p>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Getting Started Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="bg-gradient-to-r from-dark-800/50 to-secondary-900/50 backdrop-blur-sm border border-white/10 rounded-3xl p-12"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-6 gradient-text">
              Ready to Start Using AI Models?
            </h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Set up your API keys and start experimenting with different AI models to find 
              the perfect fit for your projects and workflows.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <Link
                to="/guide/quick-start"
                className="px-8 py-4 bg-gradient-to-r from-secondary-500 to-accent-500 rounded-xl font-semibold text-white text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
              >
                Setup Guide
              </Link>
              <a
                href="https://bervin.appiworks.com/app"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 border border-white/20 rounded-xl font-semibold text-white text-lg hover:bg-white/10 transition-all duration-300"
              >
                Try Now
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AvailableModelsGuide;
